import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './StatsHub.css';

const API = process.env.REACT_APP_BACKEND_URL;
const REGIONS = ['Southern', 'Orbital', 'Northern'];

const REGION_COLORS = {
  Southern: { primary: '#96C11F', glow: 'rgba(150,193,31,0.35)',  bg: 'rgba(150,193,31,0.08)' },
  Orbital:  { primary: '#00deb6', glow: 'rgba(0,222,182,0.35)',   bg: 'rgba(0,222,182,0.08)' },
  Northern: { primary: '#a78bfa', glow: 'rgba(167,139,250,0.35)', bg: 'rgba(167,139,250,0.08)' },
};

// ── Count-up ────────────────────────────────────────────────────
function useCountUp(target, duration = 1200, active = true) {
  const [value, setValue] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    if (!active || target === undefined) return;
    const start = performance.now();
    const step = (ts) => {
      const p = Math.min((ts - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * ease));
      if (p < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration, active]);
  return value;
}

// ── Stat Card ──────────────────────────────────────────────────
function StatCard({ label, value, sub, accent, delay, animate, icon }) {
  const display = useCountUp(value, 1000, animate);
  return (
    <div className="sh-stat-card" style={{ '--accent': accent, animationDelay: `${delay}ms` }} data-testid={`sh-card-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="sh-stat-icon">{icon}</div>
      <div className="sh-stat-value">{display}</div>
      <div className="sh-stat-label">{label}</div>
      {sub && <div className="sh-stat-sub">{sub}</div>}
    </div>
  );
}

// ── Radial Dial ────────────────────────────────────────────────
function RadialDial({ region, branchPct, orderPct, branchCount, orderCount, branchGrowth, orderGrowth, animate }) {
  const r = 50;
  const r2 = 38;
  const circ = 2 * Math.PI * r;
  const circ2 = 2 * Math.PI * r2;
  const offset  = circ  - (circ  * Math.min(branchPct, 100)) / 100;
  const offset2 = circ2 - (circ2 * Math.min(orderPct, 100)) / 100;
  const col = REGION_COLORS[region];
  const dispBranchPct = useCountUp(Math.round(branchPct), 1400, animate);
  const dispOrderPct  = useCountUp(Math.round(orderPct),  1600, animate);

  return (
    <div className="sh-dial-card" style={{ '--glow': col.glow, '--bg': col.bg }} data-testid={`sh-dial-${region.toLowerCase()}`}>
      <div className="sh-dial-svg-wrap">
        <svg width="130" height="130" viewBox="0 0 130 130">
          {/* Outer track — branches */}
          <circle cx="65" cy="65" r={r}  fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10" />
          <circle cx="65" cy="65" r={r}  fill="none" stroke={col.primary} strokeWidth="10" strokeLinecap="round"
            strokeDasharray={circ} strokeDashoffset={animate ? offset : circ}
            transform="rotate(-90 65 65)"
            style={{ transition: 'stroke-dashoffset 1.4s cubic-bezier(0.34,1.56,0.64,1)', filter: `drop-shadow(0 0 5px ${col.primary})` }}
          />
          {/* Inner track — orders */}
          <circle cx="65" cy="65" r={r2} fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="8" />
          <circle cx="65" cy="65" r={r2} fill="none" stroke={col.primary} strokeWidth="8" strokeLinecap="round" opacity="0.45"
            strokeDasharray={circ2} strokeDashoffset={animate ? offset2 : circ2}
            transform="rotate(-90 65 65)"
            style={{ transition: 'stroke-dashoffset 1.6s cubic-bezier(0.34,1.56,0.64,1) 0.15s' }}
          />
        </svg>
        <div className="sh-dial-inner">
          <span className="sh-dial-pct" style={{ color: col.primary }}>{dispBranchPct}%</span>
          <span className="sh-dial-count">branches</span>
        </div>
      </div>
      <div className="sh-dial-label">{region}</div>
      <div className="sh-dial-metrics">
        <div className="sh-dial-metric">
          <span className="sh-dial-metric-n">{branchCount}</span>
          <span className="sh-dial-metric-l">branches</span>
          <span className={`sh-dial-metric-g ${branchGrowth >= 0 ? 'pos' : 'neg'}`}>{branchGrowth >= 0 ? '▲' : '▼'}{Math.abs(branchGrowth)}%</span>
        </div>
        <div className="sh-dial-divider" />
        <div className="sh-dial-metric">
          <span className="sh-dial-metric-n">{orderCount}</span>
          <span className="sh-dial-metric-l">orders</span>
          <span className={`sh-dial-metric-g ${orderGrowth >= 0 ? 'pos' : 'neg'}`}>{orderGrowth >= 0 ? '▲' : '▼'}{Math.abs(orderGrowth)}%</span>
        </div>
      </div>
    </div>
  );
}

// ── Mini Trend ─────────────────────────────────────────────────
function TrendLine({ data, color, field = 'orders' }) {
  if (!data || data.length < 2) return null;
  const w = 160; const h = 40;
  const max = Math.max(...data.map(d => d[field] ?? 0), 1);
  const pts = data.map((d, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((d[field] ?? 0) / max) * (h - 4);
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={w} height={h} className="sh-trend-line">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {data.map((d, i) => {
        const x = (i / (data.length - 1)) * w;
        const y = h - ((d[field] ?? 0) / max) * (h - 4);
        return <circle key={i} cx={x} cy={y} r="2.5" fill={color} />;
      })}
    </svg>
  );
}

// ── Skeleton ───────────────────────────────────────────────────
function Skeleton({ className }) {
  return <div className={`sh-skeleton ${className || ''}`} />;
}

// ── Compare Panel ──────────────────────────────────────────────
function ComparePanel({ regionKey, data }) {
  const col = REGION_COLORS[regionKey];
  const rd = data?.regions?.[regionKey];
  return (
    <div className="sh-compare-panel" style={{ '--accent': col.primary, '--glow': col.glow }}>
      <div className="sh-compare-panel-header" style={{ color: col.primary }}>{regionKey}</div>
      <div className="sh-compare-stats">
        <div className="sh-compare-stat">
          <span className="sh-compare-stat-n">{rd?.branches_this_month ?? 0}</span>
          <span className="sh-compare-stat-l">Branches / Mo</span>
        </div>
        <div className="sh-compare-stat">
          <span className="sh-compare-stat-n">{rd?.orders_this_month ?? 0}</span>
          <span className="sh-compare-stat-l">Orders / Mo</span>
        </div>
        <div className="sh-compare-stat">
          <span className={`sh-compare-stat-n ${(rd?.orders_growth_pct ?? 0) >= 0 ? 'pos' : 'neg'}`}>
            {(rd?.orders_growth_pct ?? 0) >= 0 ? '+' : ''}{rd?.orders_growth_pct ?? 0}%
          </span>
          <span className="sh-compare-stat-l">Orders Growth</span>
        </div>
        <div className="sh-compare-stat">
          <span className={`sh-compare-stat-n ${(rd?.branch_growth_pct ?? 0) >= 0 ? 'pos' : 'neg'}`}>
            {(rd?.branch_growth_pct ?? 0) >= 0 ? '+' : ''}{rd?.branch_growth_pct ?? 0}%
          </span>
          <span className="sh-compare-stat-l">Branch Growth</span>
        </div>
      </div>
      <div className="sh-compare-trend-label">Orders trend</div>
      <TrendLine data={data?.trend} color={col.primary} field="orders" />
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────
function formatBytes(b) {
  if (!b || b < 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0, v = b;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
}

function UsageTile({ label, value, sub, accent = '#96C11F', testId }) {
  return (
    <div className="sh-usage-tile" data-testid={testId}>
      <div className="sh-usage-tile-label">{label}</div>
      <div className="sh-usage-tile-value" style={{ color: accent }}>{value}</div>
      {sub && <div className="sh-usage-tile-sub">{sub}</div>}
    </div>
  );
}

function UsageBar({ used, limit, color = '#96C11F' }) {
  if (!limit || limit <= 0) return null;
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const danger = pct >= 90 ? '#f87171' : pct >= 75 ? '#f59e0b' : color;
  return (
    <div className="sh-usage-bar">
      <div className="sh-usage-bar-track">
        <div className="sh-usage-bar-fill" style={{ width: `${pct}%`, background: danger }} />
      </div>
      <div className="sh-usage-bar-meta"><span>{pct}% used</span><span>{used.toLocaleString()} / {limit.toLocaleString()}</span></div>
    </div>
  );
}

export default function StatsHub({ token }) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [animate, setAnimate]     = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [regionA, setRegionA]     = useState('Southern');
  const [regionB, setRegionB]     = useState('Orbital');
  const [trendMode, setTrendMode] = useState('orders'); // 'orders' | 'branches'
  const [sortCol, setSortCol]     = useState('orders_total');
  const [sortDir, setSortDir]     = useState('desc');
  const [filterRegion, setFilterRegion] = useState('All');
  const [branchSort, setBranchSort]     = useState('orders_total');
  const [branchSortDir, setBranchSortDir] = useState('desc');
  const [branchFilter, setBranchFilter]   = useState('All');
  const [branchSearch, setBranchSearch]   = useState('');
  const [activeView, setActiveView]       = useState('overview'); // 'overview' | 'branches' | 'data'
  const [usage, setUsage]                 = useState(null);
  const [usageLoading, setUsageLoading]   = useState(false);
  const [usageError, setUsageError]       = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await axios.get(`${API}/api/admin/stats-hub`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
        setTimeout(() => setAnimate(true), 100);
      } catch (e) {
        console.error('Stats Hub error:', e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [token]);

  // Load Data Usage when the tab is first opened (and whenever user clicks refresh)
  const loadUsage = useCallback(async () => {
    setUsageLoading(true);
    setUsageError(null);
    try {
      const res = await axios.get(`${API}/api/admin/stats-hub/data-usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsage(res.data);
    } catch (e) {
      setUsageError(e.response?.data?.detail || e.message || 'Failed to load usage');
    } finally {
      setUsageLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (activeView === 'data' && !usage && !usageLoading) {
      loadUsage();
    }
  }, [activeView, usage, usageLoading, loadUsage]);

  const handleSort = (col, setCol, setDir, currentCol, currentDir) => {
    if (currentCol === col) setDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setCol(col); setDir('desc'); }
  };

  const SortIcon = ({ col, activeCol, dir }) => (
    <span className={`sh-sort-icon ${activeCol === col ? 'active' : ''}`}>
      {activeCol === col ? (dir === 'asc' ? '↑' : '↓') : '↕'}
    </span>
  );

  const tableRows = REGIONS
    .filter(r => filterRegion === 'All' || filterRegion === r)
    .map(r => ({ region: r, ...(data?.regions?.[r] || {}) }))
    .sort((a, b) => {
      const va = a[sortCol] ?? 0; const vb = b[sortCol] ?? 0;
      return sortDir === 'asc' ? va - vb : vb - va;
    });

  const branchRows = (data?.branches || [])
    .filter(b => branchFilter === 'All' || b.region === branchFilter)
    .filter(b => !branchSearch || b.name?.toLowerCase().includes(branchSearch.toLowerCase()))
    .sort((a, b) => {
      const va = a[branchSort] ?? 0; const vb = b[branchSort] ?? 0;
      return branchSortDir === 'asc' ? va - vb : vb - va;
    });

  return (
    <div className={`sh-root ${animate ? 'sh-animated' : ''}`}>

      {/* Header */}
      <div className="sh-header">
        <div className="sh-header-left">
          <div className="sh-header-eyebrow">Property Marketing Suite</div>
          <h1 className="sh-header-title">Adoption Insights</h1>
          <p className="sh-header-sub">Real-time overview of branch engagement and orders processed</p>
        </div>
        <div className="sh-header-right">
          <div className="sh-view-toggle">
            <button className={activeView === 'overview' ? 'active' : ''} onClick={() => setActiveView('overview')} data-testid="sh-view-overview">Overview</button>
            <button className={activeView === 'branches' ? 'active' : ''} onClick={() => setActiveView('branches')} data-testid="sh-view-branches">Branch Level</button>
            <button className={activeView === 'data' ? 'active' : ''} onClick={() => setActiveView('data')} data-testid="sh-view-data">Data Usage</button>
          </div>
          <button className={`sh-compare-toggle ${compareMode ? 'active' : ''}`} onClick={() => setCompareMode(m => !m)} data-testid="sh-compare-toggle">
            <span>⇄</span> {compareMode ? 'Exit Compare' : 'Compare Regions'}
          </button>
        </div>
      </div>

      {/* ── OVERVIEW ── */}
      {activeView === 'overview' && (
        <>
          {/* Hero cards — Branches row */}
          <div className="sh-cards-section-label">Branch Adoption</div>
          <div className="sh-cards-row sh-cards-5">
            {loading ? [0,1,2,3,4].map(i => <Skeleton key={i} className="sh-stat-card-skeleton" />) : (
              <>
                <StatCard label="This Month"     value={data?.this_month ?? 0}     accent="#96C11F" delay={0}   animate={animate} icon="◉" sub={`${data?.growth_abs >= 0 ? '+' : ''}${data?.growth_abs} vs last month`} />
                <StatCard label="Last Month"     value={data?.last_month ?? 0}     accent="#60a5fa" delay={60}  animate={animate} icon="◎" sub="Previous calendar month" />
                <StatCard label="Total Branches" value={data?.total ?? 0}          accent="#00deb6" delay={120} animate={animate} icon="◈" sub="All-time adopters" />
                <StatCard label="Branch Growth"  value={Math.abs(Math.round(data?.growth_pct ?? 0))} accent={data?.growth_pct >= 0 ? '#96C11F' : '#f87171'} delay={180} animate={animate} icon={data?.growth_pct >= 0 ? '▲' : '▼'} sub={`${data?.growth_abs >= 0 ? '+' : ''}${data?.growth_abs} branches`} />
              </>
            )}
          </div>

          {/* Hero cards — Orders row */}
          <div className="sh-cards-section-label">Orders Processed</div>
          <div className="sh-cards-row sh-cards-5">
            {loading ? [0,1,2,3,4].map(i => <Skeleton key={i} className="sh-stat-card-skeleton" />) : (
              <>
                <StatCard label="This Month"     value={data?.orders_this_month ?? 0} accent="#f59e0b" delay={0}   animate={animate} icon="◉" sub={`${data?.orders_growth_abs >= 0 ? '+' : ''}${data?.orders_growth_abs} vs last month`} />
                <StatCard label="Last Month"     value={data?.orders_last_month ?? 0} accent="#60a5fa" delay={60}  animate={animate} icon="◎" sub="Previous calendar month" />
                <StatCard label="Total Orders"   value={data?.orders_total ?? 0}       accent="#00deb6" delay={120} animate={animate} icon="◈" sub="All-time orders" />
                <StatCard label="Orders Growth"  value={Math.abs(Math.round(data?.orders_growth_pct ?? 0))} accent={data?.orders_growth_pct >= 0 ? '#f59e0b' : '#f87171'} delay={180} animate={animate} icon={data?.orders_growth_pct >= 0 ? '▲' : '▼'} sub={`${data?.orders_growth_abs >= 0 ? '+' : ''}${data?.orders_growth_abs} orders`} />
              </>
            )}
          </div>

          {/* Compare Mode */}
          {compareMode && (
            <div className="sh-compare-mode" data-testid="sh-compare-mode">
              <div className="sh-compare-row">
                <div className="sh-compare-col">
                  <select className="sh-region-select" value={regionA} onChange={e => setRegionA(e.target.value)} data-testid="sh-region-a">
                    {REGIONS.map(r => <option key={r}>{r}</option>)}
                  </select>
                  <ComparePanel regionKey={regionA} data={data} />
                </div>
                <div className="sh-compare-vs">VS</div>
                <div className="sh-compare-col">
                  <select className="sh-region-select" value={regionB} onChange={e => setRegionB(e.target.value)} data-testid="sh-region-b">
                    {REGIONS.map(r => <option key={r}>{r}</option>)}
                  </select>
                  <ComparePanel regionKey={regionB} data={data} />
                </div>
              </div>
            </div>
          )}

          {/* Regional Dials */}
          <div className="sh-section-title" style={{ marginTop: '1.5rem' }}>Regional Breakdown</div>
          <div className="sh-dials-row">
            {loading ? [0,1,2].map(i => <Skeleton key={i} className="sh-dial-skeleton" />) : (
              REGIONS.map(r => {
                const rd = data?.regions?.[r] || {};
                return (
                  <RadialDial key={r} region={r}
                    branchPct={rd.pct_of_total ?? 0}
                    orderPct={rd.orders_pct_of_total ?? 0}
                    branchCount={rd.branches_total ?? 0}
                    orderCount={rd.orders_total ?? 0}
                    branchGrowth={rd.branch_growth_pct ?? 0}
                    orderGrowth={rd.orders_growth_pct ?? 0}
                    animate={animate}
                  />
                );
              })
            )}
          </div>

          {/* Trend + Table */}
          <div className="sh-bottom-row">
            <div className="sh-trend-card">
              <div className="sh-trend-header-row">
                <span className="sh-section-title" style={{ marginBottom: 0 }}>6-Month Trend</span>
                <div className="sh-trend-toggle">
                  <button className={trendMode === 'orders' ? 'active' : ''} onClick={() => setTrendMode('orders')}>Orders</button>
                  <button className={trendMode === 'branches' ? 'active' : ''} onClick={() => setTrendMode('branches')}>Branches</button>
                </div>
              </div>
              {loading ? <Skeleton className="sh-trend-skeleton" /> : (
                <div className="sh-trend-chart">
                  {data?.trend?.map((t, i) => {
                    const vals = data.trend.map(x => x[trendMode] ?? 0);
                    const max = Math.max(...vals, 1);
                    const val = t[trendMode] ?? 0;
                    const h = Math.max((val / max) * 100, 4);
                    const isMax = val === Math.max(...vals);
                    return (
                      <div key={i} className="sh-bar-col">
                        <div className="sh-bar" style={{ height: `${h}%`, animationDelay: `${i * 80}ms`, '--bar-color': isMax ? (trendMode === 'orders' ? '#f59e0b' : '#96C11F') : `rgba(${trendMode === 'orders' ? '245,158,11' : '150,193,31'},0.35)` }} />
                        <span className="sh-bar-val">{val}</span>
                        <span className="sh-bar-label">{t.month}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="sh-table-card">
              <div className="sh-table-header-row">
                <span className="sh-section-title" style={{ marginBottom: 0 }}>Region Breakdown</span>
                <select className="sh-region-filter" value={filterRegion} onChange={e => setFilterRegion(e.target.value)} data-testid="sh-region-filter">
                  <option value="All">All Regions</option>
                  {REGIONS.map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
              {loading ? <Skeleton className="sh-table-skeleton" /> : (
                <table className="sh-table">
                  <thead>
                    <tr>
                      <th>Region</th>
                      <th className="sh-th-sort" onClick={() => handleSort('branches_this_month', setSortCol, setSortDir, sortCol, sortDir)}>Branches Mo <SortIcon col="branches_this_month" activeCol={sortCol} dir={sortDir} /></th>
                      <th className="sh-th-sort" onClick={() => handleSort('orders_this_month', setSortCol, setSortDir, sortCol, sortDir)}>Orders Mo <SortIcon col="orders_this_month" activeCol={sortCol} dir={sortDir} /></th>
                      <th className="sh-th-sort" onClick={() => handleSort('orders_growth_pct', setSortCol, setSortDir, sortCol, sortDir)}>Orders Growth <SortIcon col="orders_growth_pct" activeCol={sortCol} dir={sortDir} /></th>
                      <th className="sh-th-sort" onClick={() => handleSort('orders_total', setSortCol, setSortDir, sortCol, sortDir)}>Total Orders <SortIcon col="orders_total" activeCol={sortCol} dir={sortDir} /></th>
                      <th className="sh-th-sort" onClick={() => handleSort('branches_total', setSortCol, setSortDir, sortCol, sortDir)}>Total Branches <SortIcon col="branches_total" activeCol={sortCol} dir={sortDir} /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map(row => {
                      const col = REGION_COLORS[row.region];
                      return (
                        <tr key={row.region} className="sh-table-row" data-testid={`sh-row-${row.region?.toLowerCase()}`}>
                          <td><span className="sh-region-badge" style={{ background: col.bg, color: col.primary, borderColor: col.primary }}>{row.region}</span></td>
                          <td className="sh-td-num">{row.branches_this_month ?? 0}</td>
                          <td className="sh-td-num sh-td-orders">{row.orders_this_month ?? 0}</td>
                          <td><span className={`sh-growth-pill ${(row.orders_growth_pct ?? 0) >= 0 ? 'pos' : 'neg'}`}>{(row.orders_growth_pct ?? 0) >= 0 ? '▲' : '▼'} {Math.abs(row.orders_growth_pct ?? 0)}%</span></td>
                          <td className="sh-td-num sh-td-total">{row.orders_total ?? 0}</td>
                          <td className="sh-td-num">{row.branches_total ?? 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── BRANCH LEVEL VIEW ── */}
      {activeView === 'branches' && (
        <div className="sh-branch-view" data-testid="sh-branch-view">
          <div className="sh-branch-toolbar">
            <input className="sh-branch-search" placeholder="Search branch..." value={branchSearch} onChange={e => setBranchSearch(e.target.value)} data-testid="sh-branch-search" />
            <select className="sh-region-filter" value={branchFilter} onChange={e => setBranchFilter(e.target.value)} data-testid="sh-branch-region-filter">
              <option value="All">All Regions</option>
              {REGIONS.map(r => <option key={r}>{r}</option>)}
            </select>
            <span className="sh-branch-count">{branchRows.length} branches</span>
          </div>
          {loading ? <Skeleton className="sh-table-skeleton" style={{ height: 300 }} /> : (
            <div className="sh-branch-table-wrap">
              <table className="sh-table sh-branch-table">
                <thead>
                  <tr>
                    <th>Branch</th>
                    <th>Region</th>
                    <th className="sh-th-sort" onClick={() => handleSort('orders_this_month', setBranchSort, setBranchSortDir, branchSort, branchSortDir)}>This Month <SortIcon col="orders_this_month" activeCol={branchSort} dir={branchSortDir} /></th>
                    <th className="sh-th-sort" onClick={() => handleSort('orders_last_month', setBranchSort, setBranchSortDir, branchSort, branchSortDir)}>Last Month <SortIcon col="orders_last_month" activeCol={branchSort} dir={branchSortDir} /></th>
                    <th className="sh-th-sort" onClick={() => handleSort('orders_total', setBranchSort, setBranchSortDir, branchSort, branchSortDir)}>Total Orders <SortIcon col="orders_total" activeCol={branchSort} dir={branchSortDir} /></th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {branchRows.map((b, i) => {
                    const col = REGION_COLORS[b.region] || REGION_COLORS.Southern;
                    return (
                      <tr key={b.branch_id} className="sh-table-row" data-testid={`sh-branch-row-${i}`}>
                        <td className="sh-branch-name">{b.name}</td>
                        <td><span className="sh-region-badge sh-region-badge-sm" style={{ background: col.bg, color: col.primary, borderColor: col.primary }}>{b.region}</span></td>
                        <td className="sh-td-num">{b.orders_this_month}</td>
                        <td className="sh-td-num">{b.orders_last_month}</td>
                        <td className="sh-td-num sh-td-total">{b.orders_total}</td>
                        <td>
                          {b.active_this_month
                            ? <span className="sh-status-pill active">Active</span>
                            : b.active_last_month
                            ? <span className="sh-status-pill lapsed">Lapsed</span>
                            : <span className="sh-status-pill historic">Historic</span>
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── DATA USAGE ── */}
      {activeView === 'data' && (
        <div className="sh-data-section" data-testid="sh-data-usage">
          <div className="sh-usage-header-row">
            <div>
              <div className="sh-section-title" style={{ marginBottom: 4 }}>Data Usage</div>
              <p className="sh-usage-sub">Cloudinary credits, R2 storage footprint and monthly render volume. Refreshes on every visit.</p>
            </div>
            <button className="sh-usage-refresh-btn" onClick={loadUsage} disabled={usageLoading} data-testid="sh-usage-refresh">
              {usageLoading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          {usageError && (
            <div className="sh-usage-error" data-testid="sh-usage-error">
              Couldn't load usage: {usageError}
            </div>
          )}

          {usageLoading && !usage && (
            <div className="sh-usage-loading">Loading usage data…</div>
          )}

          {usage && (
            <>
              {/* Cloudinary */}
              <div className="sh-usage-group">
                <div className="sh-usage-group-title">
                  <span className="sh-usage-badge" style={{ background: '#3b82f6' }}>Cloudinary</span>
                  <span className="sh-usage-plan">{usage.cloudinary?.plan ? `Plan: ${usage.cloudinary.plan}` : ''}</span>
                </div>
                {usage.cloudinary ? (
                  <>
                    <div className="sh-usage-tiles">
                      <UsageTile
                        label="Credits Used"
                        value={usage.cloudinary.credits?.used?.toLocaleString() ?? '—'}
                        sub={usage.cloudinary.credits?.limit ? `of ${usage.cloudinary.credits.limit.toLocaleString()} this month` : 'This month'}
                        accent="#3b82f6"
                        testId="sh-usage-credits"
                      />
                      <UsageTile
                        label="Storage"
                        value={formatBytes(usage.cloudinary.storage_bytes || 0)}
                        sub={`${usage.cloudinary.resources || 0} resources`}
                        accent="#00deb6"
                        testId="sh-usage-storage"
                      />
                      <UsageTile
                        label="Bandwidth (month)"
                        value={formatBytes(usage.cloudinary.bandwidth_bytes || 0)}
                        sub="Delivered to users"
                        accent="#f59e0b"
                        testId="sh-usage-bandwidth"
                      />
                      <UsageTile
                        label="Transformations"
                        value={(usage.cloudinary.transformations || 0).toLocaleString()}
                        sub="This month"
                        accent="#96C11F"
                        testId="sh-usage-transforms"
                      />
                    </div>
                    {usage.cloudinary.credits?.limit > 0 && (
                      <UsageBar
                        used={usage.cloudinary.credits.used || 0}
                        limit={usage.cloudinary.credits.limit}
                        color="#3b82f6"
                      />
                    )}
                  </>
                ) : (
                  <div className="sh-usage-empty">No Cloudinary data available.</div>
                )}
              </div>

              {/* R2 */}
              <div className="sh-usage-group">
                <div className="sh-usage-group-title">
                  <span className="sh-usage-badge" style={{ background: '#F6821F' }}>Cloudflare R2</span>
                </div>
                {usage.r2 && Object.keys(usage.r2).length > 0 ? (
                  <div className="sh-usage-tiles">
                    {Object.entries(usage.r2).map(([label, v]) => (
                      <UsageTile
                        key={label}
                        label={label}
                        value={formatBytes(v.bytes)}
                        sub={`${(v.objects || 0).toLocaleString()} objects`}
                        accent="#F6821F"
                        testId={`sh-usage-r2-${label.replace(/\s+/g, '-')}`}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="sh-usage-empty">No R2 data available.</div>
                )}
              </div>

              {/* Render volume */}
              {usage.renders && (
                <div className="sh-usage-group">
                  <div className="sh-usage-group-title">
                    <span className="sh-usage-badge" style={{ background: '#96C11F' }}>Video Renders</span>
                  </div>
                  <div className="sh-usage-tiles">
                    <UsageTile label="This Month" value={usage.renders.this_month.toLocaleString()} accent="#96C11F" testId="sh-usage-renders-tm" />
                    <UsageTile label="Last Month" value={usage.renders.last_month.toLocaleString()} accent="#60a5fa" testId="sh-usage-renders-lm" />
                    <UsageTile label="All Time" value={usage.renders.total.toLocaleString()} accent="#00deb6" testId="sh-usage-renders-total" />
                  </div>
                </div>
              )}

              {usage.errors?.length > 0 && (
                <div className="sh-usage-error-list">
                  <div className="sh-usage-error-title">Partial data (some sources failed):</div>
                  {usage.errors.map((err, i) => (
                    <div key={i} className="sh-usage-error-item">{err}</div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div className="sh-footer">Region data is mocked via branch ID hash — swap for real region field when available.</div>
    </div>
  );
}
